import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

const DARK = 'dark';

// Remembered across boots so index.html can stamp the theme before the first
// frame instead of flashing a light shell until /open resolves.
const STORAGE_KEY = 'beebee_ecommerce_theme';

/**
 * Tenant theme values as they were persisted over the years: `option_field.
 * ecommerce_theme` holds either `dark` (raidacomemangi) or `theme-dark` (older
 * organizations, and the dev tenant). Both mean the same choice.
 */
const ALIASES = {
  dark: DARK,
  'theme-dark': DARK,
};

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — dark-theme gate for the storefront: normalizes the tenant
 *              flag and stamps `data-theme` + `data-scheduler-theme` on <html>,
 *              which is what activates the shared dark token block in beebee-base
 *   extends:   Service (@ember/service) — "-"
 *   template:  none
 *   key:       normalize(value) (tenant spelling -> 'dark' | null),
 *              apply(value) (stamps/clears the <html> attributes, tracks `current`,
 *              remembers the result in localStorage for the next boot)
 *   uses:      document.documentElement, window.localStorage (no service injection)
 *   siblings:  controllers/application.js (themeName getter + applyTheme action,
 *              wired through did-insert/did-update in templates/application.hbs),
 *              index.html (inline pre-paint stamp reading the same storage key)
 *   tests:     tests/unit/services/theme-test.js
 *   gotchas:   this is TENANT branding, not a user preference — unlike medical's
 *              theme service there is no localStorage and no 'system' mode. The
 *              `?theme=` query param overrides the tenant flag (support/QA).
 *              Unknown values resolve to null = light, so a typo cannot half-apply
 *              a theme.
 */
export default class ThemeService extends Service {
  /** Currently applied theme: 'dark' or null (= light). */
  @tracked current = null;

  normalize(value) {
    if (!value) {
      return null;
    }
    return ALIASES[String(value).trim().toLowerCase()] ?? null;
  }

  apply(value) {
    const theme = this.normalize(value);
    this.current = theme;

    const html = document.documentElement;
    if (theme === DARK) {
      html.dataset.theme = DARK;
      // dhtmlx scheduler 7 ships a native dark theme scoped to this attribute
      html.dataset.schedulerTheme = DARK;
    } else {
      delete html.dataset.theme;
      delete html.dataset.schedulerTheme;
    }

    // What index.html reads on the next boot to paint the shell dark from the
    // first frame. Storage can be unavailable (private mode); the theme still
    // applies for this session, only the head start is lost.
    try {
      if (theme === DARK) {
        window.localStorage?.setItem(STORAGE_KEY, DARK);
      } else {
        window.localStorage?.removeItem(STORAGE_KEY);
      }
    } catch {
      // blocked or full storage
    }

    return theme;
  }
}
